<script>
    import { Policy, User, Language } from "@Store";
    import { getContext, onMount, createEventDispatcher, tick } from "svelte";
    import { to } from "@API";
    import translations from "../utils/translations.js";

    const translationMap = {};
    translations.forEach(item => {
        if (item.eng && item.fr) translationMap[item.eng] = item.fr;
    });
    $: t = (text) => ($Language === 'fr' && translationMap[text]) ? translationMap[text] : text;

    const dispatch = createEventDispatcher();

    let { program } = getContext("PROGRAM");
    let { policyID } = getContext("CONTEXT");

    $: programData = JSON.parse(program);
    $: countries = programData?.countries || [];
    $: regions = programData?.regions || [];

    let data = {
        name: "",
        email: "",
        company: "",
        country: "",
        country_name: "",
        region: "",
        region_name: "",
        address: "",
        postal_code: "",
        city: "",
        phone: "",
    }

    let debounceTimer;
    let filteredRegions = [];

    $: billingValid = data.name && data.email && data.country && data.region && data.address && data.postal_code && data.city && data.phone;

    $: if (billingValid) {
        dispatch('billingComplete', { valid: true, data });
    } else {
        dispatch('billingComplete', { valid: false, data });
    }

    function getNamedValue(name) {
        const nv = ($Policy?.saved_named_values || []).find(n => n.name === name);
        return nv?.value?.trim() || '';
    }

    function parseMailingAddress() {
        const raw = getNamedValue('address_mailing');
        if (!raw) return {};
        try {
            return JSON.parse(raw);
        } catch {
            return {};
        }
    }

    function findCountryByName(name) {
        if (!name) return null;
        const lower = name.toLowerCase();
        return countries.find(c => c.name.toLowerCase() === lower) || null;
    }

    function findRegionByName(name, countryId) {
        if (!name || !countryId) return null;
        const lower = name.toLowerCase();
        const countryRegions = regions.filter(r => r.country == countryId);
        return countryRegions.find(r => r.name.toLowerCase() === lower) || null;
    }

    onMount(async () => {
        if ($Policy?.billing_info && $Policy.billing_info.name) {
            // Existing billing info - load it
            data = {
                name: $Policy.billing_info.name || "",
                email: $Policy.billing_info.email || $User?.email || "",
                company: $Policy.billing_info.company || "",
                country: $Policy.billing_info.country || "",
                country_name: $Policy.billing_info.country_name || "",
                region: $Policy.billing_info.region || "",
                region_name: $Policy.billing_info.region_name || "",
                address: $Policy.billing_info.address || "",
                postal_code: $Policy.billing_info.postal_code || "",
                city: $Policy.billing_info.city || "",
                phone: $Policy.billing_info.phone || "",
            }
            if (data.country) {
                filterRegions(data.country);
                await tick();
                data = { ...data };
            }
        } else {
            // Autofill from policy named values
            const contactName = getNamedValue('contact_name_title');
            const email = getNamedValue('email') || $User?.email || '';
            const phone = getNamedValue('phone_number');
            const company = getNamedValue('named_insured');
            const addr = parseMailingAddress();

            let newData = {
                ...data,
                name: contactName,
                email: email,
                phone: phone,
                company: company,
                address: addr.address || '',
                city: addr.city || '',
                postal_code: addr.postal_code || '',
            };

            // Match country from the address
            const matchedCountry = findCountryByName(addr.country);
            if (matchedCountry) {
                newData.country = matchedCountry.id;
                newData.country_name = matchedCountry.name;
                filterRegions(matchedCountry.id);

                data = { ...newData };
                await tick();

                const matchedRegion = findRegionByName(addr.state, matchedCountry.id);
                if (matchedRegion) {
                    data = { ...data, region: matchedRegion.id, region_name: matchedRegion.name };
                }
            } else {
                data = { ...newData };
            }

            // Save the autofilled data to backend
            if (data.name || data.email) {
                updateBillingInfo();
            }
        }
    });

    function filterRegions(countryId) {
        if (countryId && regions.length > 0) {
            filteredRegions = regions.filter(region => region.country == countryId);
        } else {
            filteredRegions = [];
        }
    }

    function handleCountryChange(e) {
        const selectedCountry = countries.find(c => c.id == e.target.value);
        if (selectedCountry) {
            data.country = selectedCountry.id;
            data.country_name = selectedCountry.name;

            data.region = "";
            data.region_name = "";

            filterRegions(selectedCountry.id);
        }
        updateBillingInfo();
    }

    function handleRegionChange(e) {
        const selectedRegion = filteredRegions.find(r => r.id == e.target.value);
        if (selectedRegion) {
            data.region = selectedRegion.id;
            data.region_name = selectedRegion.name;
        }
        updateBillingInfo();
    }

    async function updateBillingInfo() {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(async () => {
            let [error, response] = await to(Insurecert.API.policy($policyID).saveBilling(data));

            if (response) {
                $Policy.billing_info = response.billing_info || data;
            }

            if (error) {
                console.error("Error saving billing info:", error);
            }
        }, 500);
    }

</script>

<div class="billing-info">
    <div class="field">
        <label for="name">{t("Name")}</label>
        <input type="text" id="name" name="name" bind:value={data.name} on:input={updateBillingInfo} />
    </div>

    <div class="field">
        <label for="email">{t("Email")}</label>
        <input type="email" id="email" name="email" bind:value={data.email} on:input={updateBillingInfo} />
    </div>

    <div class="field">
        <label for="company">{t("Company")}</label>
        <input type="text" id="company" name="company" bind:value={data.company} on:input={updateBillingInfo} />
    </div>

    <div class="field">
        <label for="country">{t("Country")}</label>
        <select id="country" name="country" value={data.country} on:change={handleCountryChange}>
            <option value="">{t("Select a Country")}</option>
            {#each countries as country}
                <option value={country.id}>{country.name}</option>
            {/each}
        </select>
    </div>

    <div class="field">
        <label for="region">{t("Region")}</label>
        <select id="region" name="region" value={data.region} on:change={handleRegionChange} disabled={!data.country}>
            <option value="">{t("Select a Region")}</option>
            {#each filteredRegions as region}
                <option value={region.id}>{region.name}</option>
            {/each}
        </select>
    </div>

    <div class="field">
        <label for="address">{t("Address")}</label>
        <input type="text" id="address" name="address" bind:value={data.address} on:input={updateBillingInfo} />
    </div>

    <div class="field">
        <label for="postal_code">{t("Postal Code")}</label>
        <input type="text" id="postal_code" name="postal_code" bind:value={data.postal_code} on:input={updateBillingInfo} />
    </div>

    <div class="field">
        <label for="city">{t("City")}</label>
        <input type="text" id="city" name="city" bind:value={data.city} on:input={updateBillingInfo} />
    </div>

    <div class="field">
        <label for="phone">{t("Phone")}</label>
        <input type="tel" id="phone" name="phone" bind:value={data.phone} on:input={updateBillingInfo} />
    </div>
</div>

<style>
    .billing-info {
        padding: 1.5rem;
        background: #fff;
        display: grid;
        grid-template-columns: 1fr;
        gap: 1.25rem;
    }

    .field {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
    }

    label {
        margin: 0;
        font-size: 0.9375rem;
        font-weight: 600;
        color: #1d1d1f;
        letter-spacing: -0.01em;
    }

    select:-webkit-autofill,
    select:-webkit-autofill:hover,
    select:-webkit-autofill:focus,
    select:-webkit-autofill:active,
    input[type="tel"]:-webkit-autofill,
    input[type="tel"]:-webkit-autofill:hover,
    input[type="tel"]:-webkit-autofill:focus,
    input[type="tel"]:-webkit-autofill:active,
    input[type="email"]:-webkit-autofill,
    input[type="email"]:-webkit-autofill:hover,
    input[type="email"]:-webkit-autofill:focus,
    input[type="email"]:-webkit-autofill:active,
    input[type="text"]:-webkit-autofill,
    input[type="text"]:-webkit-autofill:hover,
    input[type="text"]:-webkit-autofill:focus,
    input[type="text"]:-webkit-autofill:active {
        -webkit-box-shadow: 0 0 0 30px white inset !important;
        -webkit-text-fill-color: #1d1d1f !important;
        transition: background-color 5000s ease-in-out 0s;
    }

    /* Green tint for autocomplete hover/focus */
    select:-webkit-autofill:hover,
    input[type="tel"]:-webkit-autofill:hover,
    input[type="email"]:-webkit-autofill:hover,
    input[type="text"]:-webkit-autofill:hover {
        -webkit-box-shadow: 0 0 0 30px rgba(49, 104, 50, 0.05) inset !important;
        border-color: var(--base-green) !important;
    }

    select:-webkit-autofill:focus,
    input[type="text"]:-webkit-autofill:focus,
    input[type="email"]:-webkit-autofill:focus,
    input[type="tel"]:-webkit-autofill:focus {
        -webkit-box-shadow: 0 0 0 30px rgba(49, 104, 50, 0.08) inset !important;
        border-color: var(--base-green) !important;
    }


    input,
    select {
        width: 100%;
        padding: 0.875rem 1rem;
        font-size: 1rem;
        border: 2px solid #e9ecef;
        border-radius: 12px;
        background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
        color: #1d1d1f;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        transition: all 0.2s ease;
        box-sizing: border-box;
    }

    input:focus,
    select:focus {
        outline: none;
        border-color: var(--base-green);
        box-shadow: 0 0 0 3px rgba(49, 104, 50, 0.1);
        background: #fff;
    }

    select {
        cursor: pointer;
        appearance: none;
        background-image: url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%231d1d1f' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: right 1rem center;
        padding-right: 3rem;
    }

    select:disabled {
        background: #f0f0f0;
        cursor: not-allowed;
        opacity: 0.6;
        border-color: #d2d2d7;
    }

    input::placeholder {
        color: #86868b;
    }

    @media (min-width: 640px) {
        .billing-info {
            padding: 2rem;
            grid-template-columns: repeat(2, 1fr);
        }

        .billing-info > .field:nth-child(1),
        .billing-info > .field:nth-child(2),
        .billing-info > .field:nth-child(3),
        .billing-info > .field:nth-child(9) {
            grid-column: span 2;
        }
    }
</style>